import parse from "date-fns/parse";
import getYear from "date-fns/getYear";
import format from "date-fns/format";
import { CalendarIcon } from "@heroicons/react/24/outline";
import EventsCalendar from "components/investorrelations/EventsCalendar";
import type { IAgendaFilterState } from "components/investorrelations/types";
import { EventsCalendarFilterRange } from "components/investorrelations/types";
import { getAgendaIssuerType } from "components/investorrelations/utils";
import NavbarLayout from "components/layouts/NavbarLayout";
import PageHeading from "components/page/PageHeading";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import classNames from "components/ui/classNames";
import Link from "next/link";
import type { NextRouter } from "next/router";
import { useRouter } from "next/router";
import { IAgendaEventType, IIssuerType, IOrderDirection } from "types/graphqlTypes";

function getFilterStateFromQuery(query: NextRouter["query"]): IAgendaFilterState {
  const { startDate, endDate, myPositions, issuerType, searchQuery, orderBy, isInSP500, isInBookmarks, period } = query;
  const safePeriods = Object.values(EventsCalendarFilterRange).map(String);
  const safeOrderDirections = Object.values(IOrderDirection).map(String);
  return {
    issuerType:
      issuerType && !Array.isArray(issuerType)
        ? getAgendaIssuerType(issuerType) || IIssuerType.BrazilianCompany
        : IIssuerType.BrazilianCompany,

    startDate: startDate ? parse(String(startDate), "yyyy-MM-dd", new Date()) : new Date(),
    endDate: endDate ? parse(String(endDate), "yyyy-MM-dd", new Date()) : new Date(),

    myPositions: myPositions ? Boolean(myPositions) : false,
    isInSP500: !!isInSP500,
    isInBookmarks: !!isInBookmarks,

    selectedFilter: safePeriods.includes(String(period))
      ? (String(period) as EventsCalendarFilterRange)
      : EventsCalendarFilterRange.Today,

    searchQuery: searchQuery ? String(searchQuery) : "",
    orderBy: safeOrderDirections.includes(String(orderBy))
      ? (String(orderBy) as IOrderDirection)
      : IOrderDirection.Ascending,
  };
}

function encodeFilterStateToQueryString(filterState: IAgendaFilterState) {
  const params = new URLSearchParams();

  params.set("startDate", format(filterState.startDate, "yyyy-MM-dd"));
  params.set("endDate", format(filterState.endDate, "yyyy-MM-dd"));

  if (filterState.myPositions) {
    params.set("myPositions", "1");
  }

  if (filterState.issuerType) {
    params.set("issuerType", String(filterState.issuerType));
  }

  if (filterState.issuerType !== IIssuerType.BrazilianCompany && filterState.isInSP500) {
    params.set("isInSP500", "1");
  }

  if (filterState.isInBookmarks) {
    params.set("isInBookmarks", "1");
  }

  if (filterState.searchQuery) {
    params.set("searchQuery", filterState.searchQuery);
  }

  if (filterState.orderBy) {
    params.set("orderBy", filterState.orderBy);
  }

  if (filterState.selectedFilter) {
    params.set("period", filterState.selectedFilter);
  }

  return params.toString();
}

function EventsCalendarPage() {
  const { query, replace } = useRouter();
  const filterState = getFilterStateFromQuery(query);
  const pageTitle = getPageTitle(filterState);
  const seoDescription = getPageDescription(filterState);

  return (
    <NavbarLayout>
      <FundamenteiMetadata title={pageTitle} description={seoDescription} />

      <div className="px-4 pb-8 font-aeonik font-normal xl:mx-auto xl:w-full xl:max-w-7xl xl:px-0">
        <PageHeading
          title="Agenda"
          description="Confira agora Agenda de Balanços mais completa do Brasil!"
          icon={<CalendarIcon className="h-6 w-6 fill-transparent" />}
          iconContainerClassName="border-blue-200 bg-blue-50 text-blue-400"
        />

        <div className="mb-4">
          <Filters
            filterBy={filterState.issuerType}
            startDate={filterState.startDate}
            endDate={filterState.endDate}
            period={filterState.selectedFilter}
            myPositions={filterState.myPositions}
            isInBookmarks={filterState.isInBookmarks}
          />
        </div>

        <div className="grid w-full grid-cols-12 gap-x-2 gap-y-3 py-4 xl:gap-x-16 xl:px-0">
          <EventsCalendar
            filterState={filterState}
            setFilterState={(filterState) => {
              const queryString = encodeFilterStateToQueryString(filterState);
              replace(`/events-calendar?${queryString}`, undefined, {
                shallow: true,
              });
            }}
            filterByEventTypes={[
              IAgendaEventType.FirstQuarterEarningsResults,
              IAgendaEventType.SecondQuarterEarningsResults,
              IAgendaEventType.ThirdQuarterEarningsResults,
              IAgendaEventType.FourthQuarterEarningsResults,
            ]}
          />
        </div>
      </div>
    </NavbarLayout>
  );
}

export default EventsCalendarPage;

function getPageTitle(filterState?: IAgendaFilterState) {
  const year = getYear(new Date());
  if (filterState) {
    if (filterState.issuerType === IIssuerType.BrazilianCompany) {
      return `Agenda de ${year} para Ações | Fundamentei`;
    }
    if (filterState.issuerType === IIssuerType.AmericanCompany) {
      return `Agenda de ${year} para Stocks | Fundamentei`;
    }
    if (filterState.issuerType === IIssuerType.AmericanReit) {
      return `Agenda de ${year} para REITs | Fundamentei`;
    }
  }
  return `Agenda de ${year} | Fundamentei`;
}
function getPageDescription(filterState?: IAgendaFilterState) {
  if (filterState) {
    if (filterState.issuerType === IIssuerType.BrazilianCompany) {
      return "Agenda completa de balanços e eventos das empresas listadas na B3";
    }
    if (filterState.issuerType === IIssuerType.AmericanCompany) {
      return "Agenda completa de balanços e eventos das Stocks listadas na NYSE e Nasdaq";
    }
    if (filterState.issuerType === IIssuerType.AmericanReit) {
      return "Agenda completa de balanços e eventos dos REITs listados na NYSE e Nasdaq";
    }
  }
  return "Agenda completa de balanços e eventos importantes das empresas listadas na B3, Nasdaq e NYSE.";
}

interface IFiltersProps {
  filterBy: string | null;
  startDate: Date;
  endDate: Date;
  period: EventsCalendarFilterRange | undefined;
  myPositions: boolean | undefined;
  isInBookmarks: boolean | undefined;
}

function Filters({ filterBy, startDate, endDate, period, myPositions, isInBookmarks }: IFiltersProps) {
  const filterByStartDate = format(startDate, "yyyy-MM-dd");
  const filterByEndDate = format(endDate, "yyyy-MM-dd");

  return (
    <div className="space-x-4">
      {[
        {
          filterBy: IIssuerType.BrazilianCompany,
          title: "Ações",
          accessibilityDescription: "Filtrar apenas por eventos das Ações",
        },
        {
          filterBy: IIssuerType.AmericanCompany,
          title: "Stocks",
          accessibilityDescription: "Filtrar apenas por eventos das Stocks",
        },
        {
          filterBy: IIssuerType.AmericanReit,
          title: "REITs",
          accessibilityDescription: "Filtrar apenas por eventos dos REITs",
        },
      ].map((node) => {
        const isActive = filterBy === node.filterBy;
        let linkHref = `/events-calendar?issuerType=${node.filterBy}&startDate=${filterByStartDate}&endDate=${filterByEndDate}&period=${period}`;

        if (myPositions) {
          linkHref = `${linkHref}&myPositions=1`;
        }

        if (isInBookmarks) {
          linkHref = `${linkHref}&isInBookmarks=1`;
        }

        return (
          <Link
            key={node.filterBy}
            href={linkHref}
            passHref={true}
            aria-label={node.accessibilityDescription}
            className={classNames(
              "font-aeonik text-sm font-medium",
              "border border-gray-200",
              "rounded-full bg-gray-100 px-4 py-2",
              "text-gray-500",
              {
                "hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700": !isActive,
                "border-transparent bg-blue-600 text-white": isActive,
              },
            )}
          >
            {node.title}
          </Link>
        );
      })}
    </div>
  );
}
